import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'
import AdminLayout from '../layouts/AdminLayout.vue'
import SettingsLayout from '../layouts/SettingsLayout.vue'
import { requireAdmin, redirectIfAuthenticated } from '../middleware/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    // If there's a saved position (browser back/forward), use it
    if (savedPosition) {
      return savedPosition
    }
    // If navigating to a hash anchor, scroll to it
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }
    // Otherwise, scroll to top
    return { top: 0, behavior: 'smooth' }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: Home,
    },
    {
      path: '/about',
      name: 'about',
      // route level code-splitting
      // this generates a separate chunk (About.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () => import('../views/AboutView.vue'),
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/RegisterView.vue'),
    },
    {
      path: '/users',
      name: 'users',
      component: () => import('../views/UsersView.vue'),
    },
    {
      path: '/shop',
      name: 'shop',
      component: () => import('../views/ProductsView.vue'),
    },
    {
      path: '/menu',
      name: 'menu',
      component: () => import('../views/RestaurantMenuView.vue'),
    },
    {
      path: '/menu/:slug',
      name: 'menu-page',
      component: () => import('../views/RestaurantMenuView.vue'),
    },
    {
      path: '/products',
      redirect: '/shop' // Redirect old products to shop
    },
    {
      path: '/excursions',
      redirect: '/shop' // Redirect old excursions to shop
    },
    {
      path: '/shop/:slug',
      name: 'product-detail',
      component: () => import('../views/ProductDetail.vue'),
    },
    {
      path: '/product/:slug',
      redirect: to => `/shop/${to.params.slug}` // Redirect old product URLs
    },
    {
      path: '/excursion/:slug',
      redirect: to => `/shop/${to.params.slug}` // Redirect old excursion URLs
    },
    {
      path: '/wishlist',
      name: 'wishlist',
      component: () => import('../views/WishlistView.vue'),
    },
    {
      path: '/cart',
      name: 'cart',
      component: () => import('../views/CartView.vue'),
    },
    {
      path: '/checkout',
      name: 'checkout',
      component: () => import('../views/CheckoutView.vue'),
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/ProfileView.vue'),
    },
    {
      path: '/orders',
      name: 'orders', 
      component: () => import('../views/OrdersView.vue'),
    },
    {
      path: '/dashboard',
      redirect: '/admin' // Redirect dashboard to admin
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('../views/ContactView.vue'),
    },
    {
      path: '/chat',
      name: 'chat',
      component: () => import('../views/ChatView.vue'),
    },
    {
      path: '/services',
      name: 'services',
      component: () => import('../views/ServicesView.vue'),
    },
    {
      path: '/services/:slug',
      name: 'service-detail',
      component: () => import('../views/ServiceDetailView.vue'),
    },
    {
      path: '/statistics',
      name: 'statistics',
      component: () => import('../views/StatisticsView.vue'),
    },
    {
      path: '/faq',
      name: 'faq',
      component: () => import('../views/FAQView.vue'),
    },
    {
      path: '/instagram-gallery',
      name: 'instagram-gallery',
      component: () => import('../views/InstagramGalleryView.vue'),
    },
    {
      path: '/order-success',
      name: 'order-success',
      component: () => import('../views/OrderSuccessView.vue'),
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('../views/PrivacyPolicy.vue'),
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/TermsOfService.vue'),
    },
    // Blog Routes
    {
      path: '/blog',
      name: 'blog',
      component: () => import('../views/PostsView.vue'),
    },
    {
      path: '/blog/:slug',
      name: 'post-detail',
      component: () => import('../views/PostDetail.vue'),
    },
    // Legacy blog routes - redirect to new format
    {
      path: '/post/:slug',
      redirect: to => `/blog/${to.params.slug}`
    },
    // Tours Routes
    {
      path: '/tours',
      name: 'tours',
      component: () => import('../views/ToursView.vue'),
    },
    {
      path: '/tours/:slug',
      name: 'tour-detail',
      component: () => import('../views/TourDetail.vue'),
    },
    // Admin Routes
    {
      path: '/admin',
      component: AdminLayout,
      beforeEnter: requireAdmin,
      children: [
        {
          path: '',
          name: 'admin-dashboard',
          component: () => import('../views/admin/AdminDashboard.vue'),
        },
        {
          path: 'shop',
          name: 'admin-shop',
          component: () => import('../views/AdminProductsView.vue'),
        },
        {
          path: 'products',
          redirect: '/admin/shop' // Redirect old admin products to shop
        },
        {
          path: 'categories',
          name: 'admin-categories',
          component: () => import('../views/admin/AdminCategories.vue'),
        },
        {
          path: 'brands',
          name: 'admin-brands',
          component: () => import('../views/admin/AdminBrands.vue'),
        },
        {
          path: 'posts',
          name: 'admin-posts',
          component: () => import('../views/AdminPosts.vue'),
        },
        {
          path: 'post-categories',
          name: 'admin-post-categories',
          component: () => import('../views/admin/AdminPostCategories.vue'),
        },
        {
          path: 'post-templates',
          name: 'admin-post-templates',
          component: () => import('../views/admin/AdminTemplates.vue'),
        },
        {
          path: 'pages',
          name: 'admin-pages',
          component: () => import('../views/admin/AdminPages.vue'),
        },
        {
          path: 'product-addons',
          name: 'admin-product-addons',
          component: () => import('../views/admin/ProductAddons.vue'),
        },
        {
          path: 'bookings',
          name: 'admin-bookings',
          component: () => import('../views/admin/AdminBookings.vue'),
        },
        {
          path: 'tours',
          name: 'admin-tours',
          component: () => import('../views/admin/AdminTours.vue'),
        },
        {
          path: 'tour-seats',
          name: 'admin-tour-seats',
          component: () => import('../views/admin/TourSeatsView.vue'),
        },
        {
          path: 'tour-bookings',
          name: 'admin-tour-bookings',
          component: () => import('../views/admin/AdminTourBookings.vue'),
        },
        {
          path: 'tour-categories',
          name: 'admin-tour-categories',
          component: () => import('../views/admin/AdminTourCategories.vue'),
        },
        {
          path: 'tour-destinations',
          name: 'admin-tour-destinations',
          component: () => import('../views/admin/AdminTourDestinations.vue'),
        },
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('../views/admin/AdminUsers.vue'),
        },
        {
          path: 'home-builder',
          name: 'admin-home-builder',
          component: () => import('../views/admin/HomeBuilder.vue'),
        },
        {
          path: 'notifications',
          name: 'admin-notifications',
          component: () => import('../views/admin/AdminNotifications.vue'),
        },
        {
          path: 'emails',
          name: 'admin-emails',
          component: () => import('../views/admin/AdminEmails.vue'),
        },
        {
          path: 'reviews',
          name: 'admin-reviews',
          component: () => import('../views/AdminReviews.vue'),
        },
        {
          path: 'media',
          name: 'admin-media',
          component: () => import('../views/admin/AdminMedia.vue'),
        },
        {
          path: 'tutorial',
          name: 'admin-tutorial',
          component: () => import('../views/admin/AdminTutorial.vue'),
        },
        {
          path: 'menu-guide',
          name: 'admin-menu-guide',
          component: () => import('../views/admin/AdminMenuGuide.vue'),
        },
        {
          path: 'analytics',
          name: 'admin-analytics',
          component: () => import('../views/admin/AdminAnalytics.vue'),
        },
        {
          path: 'database',
          name: 'admin-database',
          component: () => import('../views/admin/AdminDatabase.vue'),
        },
        {
          path: 'profile',
          name: 'admin-profile',
          component: () => import('../views/ProfileView.vue'),
        },
        {
          path: 'settings',
          component: SettingsLayout,
          children: [
            {
              path: '',
              redirect: { name: 'admin-settings-content' }
            },
            {
              path: 'content',
              name: 'admin-settings-content',
              component: () => import('../views/admin/settings/ContentSettings.vue'),
            },
            {
              path: 'gallery',
              name: 'admin-settings-gallery',
              component: () => import('../views/admin/settings/GallerySettings.vue'),
            },
            {
              path: 'masonry',
              name: 'admin-settings-masonry',
              component: () => import('../views/admin/settings/MasonrySettings.vue'),
            },
            {
              path: 'layout',
              name: 'admin-settings-layout',
              component: () => import('../views/admin/settings/LayoutSettings.vue'),
            },
            {
              path: 'sidebar',
              name: 'admin-settings-sidebar',
              component: () => import('../views/admin/settings/SidebarSettings.vue'),
            },
            {
              path: 'sidebar-widgets',
              name: 'admin-sidebar-widgets',
              component: () => import('../views/admin/SidebarWidgets.vue'),
            },
            {
              path: 'navigation',
              name: 'admin-settings-navigation',
              component: () => import('../views/admin/NavigationSettings.vue'),
            },
            {
              path: 'footer',
              name: 'admin-settings-footer',
              component: () => import('../views/admin/settings/FooterSettings.vue'),
            },
            {
              path: 'system',
              name: 'admin-settings-system',
              component: () => import('../views/admin/settings/SystemSettings.vue'),
            },
            {
              path: 'ssl',
              name: 'admin-settings-ssl',
              component: () => import('../views/admin/settings/SSLSettings.vue'),
            },
            {
              path: 'facebook-auto-post',
              name: 'admin-settings-facebook-auto-post',
              component: () => import('../views/admin/settings/FacebookAutoPostSettings.vue'),
            },
            {
              path: 'instagram',
              name: 'admin-settings-instagram',
              component: () => import('../views/admin/settings/InstagramSettings.vue'),
            },

            {
              path: 'email',
              name: 'admin-settings-email',
              component: () => import('../views/admin/settings/EmailSettings.vue'),
            },
            {
              path: 'contact',
              name: 'admin-settings-contact',
              component: () => import('../views/admin/settings/ContactSettings.vue'),
            },
            {
              path: 'seo',
              name: 'admin-settings-seo',
              component: () => import('../views/admin/settings/UnifiedSEO.vue'),
            },
            {
              path: 'schema',
              name: 'admin-settings-schema',
              component: () => import('../views/admin/settings/SchemaSettings.vue'),
            },
            {
              path: 'blog',
              name: 'admin-settings-blog',
              component: () => import('../views/admin/settings/BlogSettings.vue'),
            },
            {
              path: 'restaurant-menus',
              name: 'admin-settings-restaurant-menus',
              component: () => import('../views/admin/settings/AdminRestaurantMenus.vue'),
            },
          ],
        },
      ],
    },
    // Dynamic Pages Route - catch pages by slug (before catch-all)
    {
      path: '/:slug',
      name: 'page',
      component: () => import('../views/PageView.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('../views/NotFound.vue'),
    },
  ],
})

export default router
